import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sprout, User, Building2, ArrowLeft, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from "../components/LanguageSwitcher";
import { QRCodeSVG } from "qrcode.react";

// Import modular auth components
import FarmerLoginForm from "../components/auth/FarmerLoginForm";
import OfficialLoginForm from "../components/auth/OfficialLoginForm";
import FarmerRegisterForm from "../components/auth/FarmerRegisterForm";
import OfficialRegisterForm from "../components/auth/OfficialRegisterForm";

export default function Login({ setUser }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // User type selection state
  const [userType, setUserType] = useState(null); // "farmer" | "official"
  const [activeTab, setActiveTab] = useState("login");
  const [isInstalledPWA, setIsInstalledPWA] = useState(false);

  // Detect if running as installed PWA (standalone mode)
  useState(() => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');
    setIsInstalledPWA(isStandalone);
  });
  
  // Form data state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phone: "",
    name: "",
    guardianName: "",
    villageOfResidence: "",
  });

  // Initialize form handlers
  const farmerLogin = FarmerLoginForm({ formData, setFormData, t });
  const officialLogin = OfficialLoginForm({ formData, setFormData, t });
  const farmerRegister = FarmerRegisterForm({ formData, setFormData, t });
  const officialRegister = OfficialRegisterForm({ formData, setFormData, t });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    let result;
    
    if (userType === "farmer") {
      if (activeTab === "login") {
        result = await farmerLogin.handleSubmit();
      } else {
        result = await farmerRegister.handleSubmit();
      }
    } else {
      if (activeTab === "login") {
        result = await officialLogin.handleSubmit();
      } else {
        result = await officialRegister.handleSubmit();
      }
    }
    
    if (result?.success && !result?.waitForOtp) {
      localStorage.setItem("token", result.token);
      
      // Save selected department for officials (for filtering applications)
      if (userType === "official" && officialLogin.selectedDepartment) {
        localStorage.setItem("selectedDepartment", officialLogin.selectedDepartment);
      }
      
      // Check if password change is required (for officials)
      if (result.mustChangePassword) {
        localStorage.setItem("mustChangePassword", "true");
      }
      
      setUser(result.user);
      localStorage.setItem("userName", result.user.name || "");
      
      // Navigate based on user role
      if (result.user.role === "farmer") {
        navigate("/farmer/dashboard");
      } else {
        navigate("/official/dashboard");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      password: "",
      phone: "",
      name: "",
      guardianName: "",
      villageOfResidence: "",
    });
  };

  const handleBack = () => {
    setUserType(null);
    resetForm();
    setActiveTab("login");
  };

  const getButtonText = () => {
    if (userType === "farmer") {
      return activeTab === "login" ? farmerLogin.getButtonText() : farmerRegister.getButtonText();
    } else {
      return activeTab === "login" ? officialLogin.getButtonText() : officialRegister.getButtonText();
    }
  };

  const isLoading = () => {
    if (userType === "farmer") {
      return activeTab === "login" ? farmerLogin.loading : farmerRegister.loading;
    } else {
      return activeTab === "login" ? officialLogin.loading : officialRegister.loading;
    }
  };

  // MLA Photo URL - loaded from environment variable for deployment portability
  const MLA_PHOTO_URL = process.env.REACT_APP_MLA_PHOTO_URL || "https://customer-assets.emergentagent.com/job_dbddb83d-911f-4b4b-8f2f-33ed387b98d3/artifacts/81mb43a6_MLAPhoto2.jpg";

  // User Type Selection Screen
  if (!userType) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row">
        {/* Desktop: Left panel with MLA Balakrishna image */}
        <div className="hidden lg:flex lg:w-1/2 h-screen relative">
          <div className="w-full h-full bg-stone-900 flex items-center justify-center">
            <img 
              src={MLA_PHOTO_URL}
              alt="MLA Balakrishna"
              className="w-full h-full object-contain"
            />
          </div>
          
          {/* App Name and Tagline - superimposed at bottom */}
          <div className="absolute bottom-0 left-0 right-0 bg-emerald-800/95 backdrop-blur-sm px-8 py-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-2xl font-manrope font-bold tracking-tight mb-1 text-white">
                {t('appName')}
              </h1>
              <p className="text-sm text-emerald-100 font-inter">
                {t('tagline')}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Mobile: Header with app name */}
        <div className="lg:hidden bg-emerald-700 px-4 py-6 text-center">
          <h1 className="text-2xl font-manrope font-bold text-white tracking-tight">
            {t('appName')}
          </h1>
          <p className="text-sm text-emerald-100 mt-1">
            {t('tagline')}
          </p>
        </div>

        {/* Right panel with user type selection */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-stone-100 flex-1">
          {/* Language Switcher */}
          <div className="absolute top-4 right-4">
            <LanguageSwitcher />
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-lg"
          >
            {/* Logo and Title - Only on Desktop since mobile has header */}
            <div className="text-center mb-8 lg:block hidden">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-800 rounded-2xl mb-4 shadow-lg">
                <Sprout className="w-8 h-8 text-white" />
              </div>
              <p className="text-emerald-700">{t('login.selectUserType')}</p>
            </div>

            {/* Mobile Title */}
            <div className="text-center mb-6 lg:hidden">
              <p className="text-emerald-700">{t('login.selectUserType')}</p>
            </div>

            {/* User Type Selection Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card 
                  className="cursor-pointer hover:border-emerald-500 hover:shadow-lg transition-all bg-white/80 backdrop-blur"
                  onClick={() => setUserType("farmer")}
                  data-testid="farmer-login-btn"
                >
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 rounded-xl mb-4">
                      <User className="w-7 h-7 text-emerald-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-emerald-900 mb-1">{t('login.farmer')}</h3>
                    <p className="text-sm text-emerald-600">{t('login.farmerDescription')}</p>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Card 
                  className="cursor-pointer hover:border-emerald-500 hover:shadow-lg transition-all bg-white/80 backdrop-blur"
                  onClick={() => setUserType("official")}
                  data-testid="official-login-btn"
                >
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 rounded-xl mb-4">
                      <Building2 className="w-7 h-7 text-emerald-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-emerald-900 mb-1">{t('login.official')}</h3>
                    <p className="text-sm text-emerald-600">{t('login.officialDescription')}</p>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* QR Code - Install App (hidden when running as installed PWA) */}
            {!isInstalledPWA && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-8 flex flex-col items-center"
              data-testid="qr-code-section"
            >
              <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 inline-flex flex-col items-center">
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-semibold text-emerald-800">
                    {t('login.scanToInstall') || 'Scan to install on your phone'}
                  </span>
                </div>
                <QRCodeSVG
                  value={window.location.origin}
                  size={120}
                  bgColor="#ffffff"
                  fgColor="#065f46"
                  level="M"
                  includeMargin={false}
                />
                <p className="text-[10px] text-stone-400 mt-2 text-center max-w-[160px]">
                  {t('login.qrHint') || 'Open camera & scan this code'}
                </p>
              </div>
            </motion.div>
            )}

            {/* Footer - Commented out pending client discussion */}
            {/* <p className="text-center text-orange-600 font-semibold text-sm mt-8">
              {t('login.poweredBy')}
            </p> */}
          </motion.div>
        </div>
      </div>
    );
  }

  // Login/Register Form Screen
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Desktop: Left panel with MLA Balakrishna image */}
      <div className="hidden lg:flex lg:w-1/2 h-screen relative">
        <div className="w-full h-full bg-stone-900 flex items-center justify-center">
          <img 
            src={MLA_PHOTO_URL}
            alt="MLA Balakrishna"
            className="w-full h-full object-contain"
          />
        </div>
        
        {/* App Name and Tagline - superimposed at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-emerald-800/95 backdrop-blur-sm px-8 py-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-2xl font-manrope font-bold tracking-tight mb-1 text-white">
              {t('appName')}
            </h1>
            <p className="text-sm text-emerald-100 font-inter">
              {t('tagline')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Mobile: Header with app name */}
      <div className="lg:hidden bg-emerald-700 px-4 py-6 text-center">
        <h1 className="text-2xl font-manrope font-bold text-white tracking-tight">
          {t('appName')}
        </h1>
        <p className="text-sm text-emerald-100 mt-1">
          {t('tagline')}
        </p>
      </div>

      {/* Right panel with form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8 bg-stone-100 flex-1">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={handleBack}
          className="mb-4 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>

        {/* Portal Title - Only on Desktop */}
        <div className="text-center mb-6 hidden lg:block">
          <p className="text-emerald-700 text-lg font-medium">
            {userType === "farmer" ? t('login.farmerPortal') : t('login.officialPortal')}
          </p>
        </div>

        {/* Form Card */}
        <Card className="bg-white/90 backdrop-blur shadow-xl border-0">
          <CardHeader className="pb-2">
            <Tabs value={activeTab} onValueChange={(val) => { setActiveTab(val); resetForm(); }}>
              <TabsList className="grid w-full grid-cols-2 bg-stone-100">
                <TabsTrigger 
                  value="login" 
                  className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white"
                  data-testid="tab-login"
                >
                  {t('login.signIn')}
                </TabsTrigger>
                <TabsTrigger 
                  value="register"
                  className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white"
                  data-testid="tab-register"
                >
                  {t('login.register')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>

          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Render appropriate form based on user type and tab */}
              {userType === "farmer" ? (
                activeTab === "login" ? farmerLogin.render : farmerRegister.render
              ) : (
                activeTab === "login" ? officialLogin.render : officialRegister.render
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                disabled={isLoading() || (activeTab === "login" && userType === "official" && !officialLogin.selectedDepartment)}
                data-testid="submit-btn"
              >
                {getButtonText()}
              </Button>
            </form>

            {/* Alternative Action */}
            <div className="mt-4 text-center text-sm text-emerald-600">
              {activeTab === "login" ? (
                <p>
                  {t('login.noAccount')}{" "}
                  <button
                    type="button"
                    onClick={() => { setActiveTab("register"); resetForm(); }}
                    className="text-emerald-800 font-medium hover:underline"
                  >
                    {t('login.registerHere')}
                  </button>
                </p>
              ) : (
                <p>
                  {t('login.hasAccount')}{" "}
                  <button
                    type="button"
                    onClick={() => { setActiveTab("login"); resetForm(); }}
                    className="text-emerald-800 font-medium hover:underline"
                  >
                    {t('login.signInHere')}
                  </button>
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </motion.div>
      </div>
    </div>
  );
}
